import { createSelector } from "@reduxjs/toolkit";

import { createDeepEqualSelector } from "../utils/createDeepEqualSelector";
import { selectAllAccounts } from "../accounts";
import { formatBulkPayment } from "../bulkPayments/helpers";

import type { RootState } from "../store";

// Basic selectors
export const selectFileImportPaymentsState = (state: RootState) =>
  state.fileImportPayments;

export const selectFileImportPaymentsById = createSelector(
  [selectFileImportPaymentsState],
  (fileImportPaymentsState) => fileImportPaymentsState.byId,
);

export const selectFileImportPaymentsList = createSelector(
  [selectFileImportPaymentsState],
  (fileImportPaymentsState) => fileImportPaymentsState.list,
);

export const selectFileImportPaymentsTotal = createSelector(
  [selectFileImportPaymentsState],
  (fileImportPaymentsState) => fileImportPaymentsState.total,
);

export const selectFileImportPaymentsFilters = createSelector(
  [selectFileImportPaymentsState],
  (fileImportPaymentsState) => fileImportPaymentsState.filters,
);

export const selectFileImportPaymentsPageMap = createSelector(
  [selectFileImportPaymentsState],
  (fileImportPaymentsState) => fileImportPaymentsState.pageMap,
);

// Computed selectors
export const selectAllFileImportPayments = createDeepEqualSelector(
  [selectFileImportPaymentsById, selectFileImportPaymentsList],
  (byId, list) => list.map((id) => byId[id]).filter(Boolean),
);

export const selectFileImportPaymentById = (uuid: string) =>
  createSelector([selectFileImportPaymentsById], (byId) => byId[uuid]);

export const selectFormattedFileImportPayments = createDeepEqualSelector(
  [
    selectFileImportPaymentsList,
    selectFileImportPaymentsById,
    selectAllAccounts,
  ],
  (list, byId, accounts) => {
    return list
      ?.map((id) => {
        const activity = byId[id];
        if (!activity) return null;
        return formatBulkPayment(activity, accounts);
      })
      ?.filter(Boolean);
  },
);

export const selectFormattedPaginatedFileImportPayments =
  createDeepEqualSelector(
    [
      selectFileImportPaymentsPageMap,
      selectFileImportPaymentsById,
      selectAllAccounts,
      (_, pageNumber: number) => pageNumber,
    ],
    (pageMap, byId, accounts, pageNumber) => {
      const ids = pageMap[pageNumber] ?? [];

      return ids
        ?.map((id) => {
          const activity = byId[id];
          if (!activity) return null;
          return formatBulkPayment(activity, accounts);
        })
        ?.filter(Boolean);
    },
  );
